import {
  Box,
  Flex,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
} from '@chakra-ui/react';
import React, { ReactNode, Suspense, useRef, useState } from 'react';
import { chatPersonaMenuState } from './flowDBlock/chatPersonaMenuState';
import { useSnapshot } from 'valtio';
import { NodeViewProps } from '@tiptap/core';
import { LucideUndo2 } from 'lucide-react';
import { PersonasMenuList } from './flowDBlock/PersonasMenuList';
import { ConversationMessage } from '@duke-hq/ui/components/Conversation/ConversationMessage';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import { usePersonaResolver } from '../PersonaResolverContext';
import { ItemActionsMenu } from '../nodes/blocknode/ItemActionsMenu';
import { moveBlock } from '../nodes/blocknode/moveBlock';
import { unsetBlockPersona } from '../nodes/blocknode/unsetBlockPersona';
import { MENU_Z_INDEX } from '../nodes/blocknode/AnchoredPanel';
import { flowEditorState } from '../flowEditorState';

// -- PersonaNodeWrapper ----------------------------------------------------------
// Renders one editable message of the flow editor as a conversation turn: the
// same ConversationMessage bubble the student runtime and the static node card
// use, so editing looks like editing the conversation itself. The editable
// content (NodeViewContent, media, files) is the bubble's body; the editor-only
// affordances (drag handle, persona/delete menu) appear beside the bubble on
// hover.
export function PersonaNodeWrapper({
  children,
  disablePersona,
  ...props
}: NodeViewProps & {
  children: React.ReactNode;
  // Backward compatibility to support File Upload nodes that don't have persona ids attached
  disablePersona?: boolean;
}) {
  const { openForNode } = useSnapshot(chatPersonaMenuState);
  const { node, updateAttributes, deleteNode } = props;
  const [hovered, setHovered] = useState(false);
  const [personaSearch, setPersonaSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const isOpen = openForNode === node;
  const { getPersonaById } = usePersonaResolver();

  const personaId = typeof node.attrs.personaId === 'string' ? node.attrs.personaId : '';
  const persona = getPersonaById(personaId);

  // Where this block sits in the document: nested blocks (legacy content can
  // have e.g. a file inside a dBlock) render flat inside the parent's bubble
  // rather than as a bubble of their own, and the first message of a speaker
  // run carries the name and avatar, with continuing messages aligned
  // underneath. Same grouping as the static card and the student conversation.
  const { isNested, isFirstOfRun, canMoveUp, canMoveDown, previousPersonaId } = (() => {
    const pos = props.getPos();
    if (typeof pos !== 'number') {
      return {
        isNested: false,
        isFirstOfRun: true,
        canMoveUp: false,
        canMoveDown: false,
        previousPersonaId: null,
      };
    }
    const $pos = props.editor.state.doc.resolve(pos);
    if ($pos.depth !== 0) {
      return {
        isNested: true,
        isFirstOfRun: false,
        canMoveUp: false,
        canMoveDown: false,
        previousPersonaId: null,
      };
    }
    const index = $pos.index(0);
    const previous = index > 0 ? props.editor.state.doc.maybeChild(index - 1) : null;
    // Normalise the previous block's personaId the same way as the current one
    // (missing attrs become '') so the grouping matches StaticBlockContent.
    const prevId = typeof previous?.attrs.personaId === 'string' ? previous.attrs.personaId : '';
    return {
      isNested: false,
      isFirstOfRun: !previous || prevId !== personaId,
      canMoveUp: index > 0,
      canMoveDown: index < props.editor.state.doc.childCount - 1,
      previousPersonaId: previous ? prevId : null,
    };
  })();

  // "Unset persona" reverts this message to the persona of the message above so
  // it rejoins that run (every block must have a persona; there is no true
  // null). Only meaningful when there's a previous block whose persona differs.
  const canUnsetPersona = previousPersonaId !== null && previousPersonaId !== personaId;

  const showPersona = !disablePersona && isFirstOfRun;
  const actionsVisible = hovered || isOpen;

  const move = (direction: 'up' | 'down') => {
    const pos = props.getPos();
    if (typeof pos === 'number') {
      moveBlock(props.editor, pos, direction);
    }
  };

  // Re-derive the unset from live editor state at click time, not a render-time
  // capture: the menu stays open across assignments, so a captured value goes
  // stale and unset can write back an earlier persona.
  const unsetPersona = () => {
    const pos = props.getPos();
    if (typeof pos === 'number') {
      unsetBlockPersona(props.editor, pos);
    }
    chatPersonaMenuState.close();
  };

  if (isNested) {
    return (
      <Flex className="content" data-draggable width="100%">
        {children}
      </Flex>
    );
  }

  return (
    <Box
      width="100%"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={(ev) => {
        if (openForNode) {
          chatPersonaMenuState.close();
        }
        ev.stopPropagation();
        return false;
      }}
    >
      <ConversationMessage
        persona={persona}
        showName={showPersona}
        showAvatar={showPersona}
        // Continuing messages show the muted carry-over avatar so every message
        // stays individually re-assignable.
        continuesPersona={!disablePersona && !isFirstOfRun}
        // Clicking the avatar opens the persona picker right there.
        wrapPersona={
          disablePersona
            ? undefined
            : (slot: ReactNode) => (
                <Menu
                  closeOnSelect={false}
                  isOpen={isOpen}
                  // Focus the search on open (rather than Chakra's default of
                  // the first menu item) so you can type a persona name straight
                  // away.
                  initialFocusRef={searchRef}
                  onOpen={() => {
                    chatPersonaMenuState.open(node);
                  }}
                  onClose={() => {
                    chatPersonaMenuState.close();
                    setPersonaSearch('');
                  }}
                  closeOnBlur={false} // keep open while typing in the persona search
                >
                  <MenuButton aria-label="Change persona">{slot}</MenuButton>
                  {/* Portal the list to the document root so it renders at full
                      size outside the ReactFlow canvas transform (otherwise it
                      shrinks and clips with the zoom level), matching the
                      message overflow menu. */}
                  <Portal>
                    <MenuList zIndex={MENU_Z_INDEX}>
                      {/* Search heads the menu so it reads as a persona picker;
                          it filters the list below and is sticky while scrolling. */}
                      <Box
                        position="sticky"
                        top={0}
                        bg="bg.surface"
                        zIndex={1}
                        px={2}
                        pb={2}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <SearchInput
                          ref={searchRef}
                          urlSync={false}
                          delay={0}
                          placeholder="Search personas..."
                          onCommit={setPersonaSearch}
                        />
                      </Box>
                      {/* Reverts this message to the persona above so it rejoins
                          that run. Only shown when there's something to unset:
                          the avatar you clicked already tells you whether a
                          persona is set, so a disabled item here is just noise. */}
                      {canUnsetPersona ? (
                        <>
                          <MenuItem icon={<LucideUndo2 size={16} />} onClick={unsetPersona}>
                            Unset persona
                          </MenuItem>
                          <MenuDivider />
                        </>
                      ) : null}
                      <Box maxH={pxToRem(400)} overflowY="auto">
                        <Suspense fallback={<Spinner />}>
                          <PersonasMenuList
                            search={personaSearch}
                            onSelected={(personaId) => {
                              updateAttributes({ personaId });
                              // Track the last persona the author chose, so a new
                              // block with no source to inherit from starts as
                              // this speaker rather than the library default.
                              flowEditorState.defaultPersonaId = personaId;
                              chatPersonaMenuState.close();
                            }}
                          />
                        </Suspense>
                      </Box>
                    </MenuList>
                  </Portal>
                </Menu>
              )
        }
        // Messages reorder via the menu's Move up/down (no drag grip): in-editor
        // drag was too fiddly. Same menu as the option pills.
        actions={
          <Box contentEditable={false} pointerEvents={actionsVisible ? 'auto' : 'none'}>
            <ItemActionsMenu
              label="Message actions"
              isVisible={actionsVisible}
              canMoveUp={canMoveUp}
              canMoveDown={canMoveDown}
              onMoveUp={() => move('up')}
              onMoveDown={() => move('down')}
              onDelete={deleteNode}
            />
          </Box>
        }
      >
        <Flex className="content" data-draggable>
          {children}
        </Flex>
      </ConversationMessage>
    </Box>
  );
}
